/**
 * DOCS SOCIETÁRIOS: AS REGRAS DA ESCRITA (10/10/2026, medição do item 15). Funções puras que a CorporateDocsTab usa e os
 * testes cobram dos dois lados. Confirms na forma do useConfirmarPerigo: 1º parágrafo = título curto, o resto = corpo.
 */
import { SIGNERS } from '../config/company';

const STATUS_FIXOS = ['Aguardando Assinatura', 'Assinado', 'Vigente'];

/**
 * D6: o sócio é identificado pelo E-MAIL da conta, o mesmo critério do servidor (corporate-docs.service approve). A tela
 * decidia pelo NOME (`includes('heitor')`): a conta viewer "Heitor Fernandes" via o botão e tomava a recusa.
 */
export function socioDoUsuario(user: any): 'fabio' | 'heitor' | null {
  const email = String(user?.email || '').toLowerCase();
  if (!email) return null;
  if (email === String(SIGNERS.fabio.email).toLowerCase()) return 'fabio';
  if (email === String(SIGNERS.heitor.email).toLowerCase()) return 'heitor';
  return null;
}

export function podeAprovar(doc: any, user: any): boolean {
  if (doc?.status !== 'Minuta') return false;
  const socio = socioDoUsuario(user);
  if (socio === 'fabio') return !doc.approvedByFabio;
  if (socio === 'heitor') return !doc.approvedByHeitor;
  return false;
}

/**
 * D2: nova versão só em Minuta. O servidor troca o arquivo e mantém o status e as aprovações: em Aprovado, o arquivo que
 * vai ao ClickSign passa a ser um que nenhum sócio aprovou; em Aguardando/Assinado/Vigente, o registro aponta para um
 * arquivo que não é o assinado. Fora de Minuta, a tela trava com o motivo (voltar para Minuta é pendência do backend).
 */
export function porqueNaoNovaVersao(doc: any): string | null {
  if (doc?.status === 'Minuta') return null;
  return `O documento está ${doc?.status}: uma versão nova por cima faria valer um arquivo que os sócios não aprovaram`
    + (STATUS_FIXOS.includes(doc?.status) ? ' nem assinaram' : '')
    + '. Nova versão só em Minuta.';
}

/** D2, em Minuta: as aprovações já dadas seguem valendo para o arquivo novo (o servidor não as zera). */
export function avisoDaNovaVersao(doc: any): string | null {
  const quem = [doc?.approvedByFabio ? 'Fabio' : null, doc?.approvedByHeitor ? 'Heitor' : null].filter(Boolean);
  if (quem.length === 0) return null;
  return `A aprovação de ${quem.join(' e ')} continua valendo para o arquivo novo: o servidor não a desfaz. Se o conteúdo mudou, peça nova aprovação.`;
}

/**
 * D3: o que cada transição manual faz. Sair de Aguardando Assinatura por aqui NÃO cancela o envelope do ClickSign: se os
 * signatários terminarem, o webhook marca o documento como Assinado (inclusive um Revogado). Fica travado, e o caminho é
 * "Cancelar assinatura" (que cancela o envelope e volta para Aprovado).
 */
export function consequenciaDaTransicao(de: string, para: string): { texto: string; travado: boolean } {
  if (de === 'Aguardando Assinatura') {
    return {
      texto: 'Sair de Aguardando Assinatura por aqui não cancela o envelope no ClickSign: se os signatários terminarem, o documento volta a Assinado. Use "Cancelar assinatura" no detalhe (cancela o envelope e volta para Aprovado); depois, se for o caso, revogue.',
      travado: true,
    };
  }
  if (para === 'Assinado') {
    return { texto: 'Marca como Assinado SEM assinatura no ClickSign e sem PDF assinado. Use só para documento assinado fora do sistema.', travado: false };
  }
  if (para === 'Revogado') {
    return { texto: 'Revogado é final: o documento deixa de valer e não sai mais desse status.', travado: false };
  }
  if (para === 'Vigente') {
    return { texto: 'Marca o documento assinado como em vigor.', travado: false };
  }
  return { texto: '', travado: false };
}

/** D4: excluir apaga o arquivo; documento enviado, assinado ou vigente se revoga, não se exclui. */
export function porqueNaoExcluir(doc: any): string | null {
  if (doc?.status === 'Aguardando Assinatura') return 'Está aguardando assinatura: cancele a assinatura antes. Excluir apagaria o arquivo que está no ClickSign.';
  if (doc?.status === 'Assinado' || doc?.status === 'Vigente') {
    return `Está ${doc.status}: excluir apagaria o arquivo e deixaria o PDF assinado sem registro. Revogue em vez de excluir.`;
  }
  return null;
}

export function descricaoDaExclusao(doc: any): string {
  const aprov = [doc?.approvedByFabio ? 'Fabio' : null, doc?.approvedByHeitor ? 'Heitor' : null].filter(Boolean);
  return `${doc?.status} · v${doc?.version}${aprov.length ? ` · aprovado por ${aprov.join(' e ')}` : ''}${doc?.signedPdfS3Key ? ' · tem PDF assinado' : ''}. O arquivo é apagado do armazenamento.`;
}

/** D5: editar documento assinado ou vigente muda só o cadastro. */
export function avisoDaEdicao(doc: any): string | null {
  if (doc?.status === 'Assinado' || doc?.status === 'Vigente' || doc?.status === 'Aguardando Assinatura') {
    return `O documento está ${doc.status}: aqui muda só o cadastro (título, categoria, datas), não o arquivo ${doc.status === 'Aguardando Assinatura' ? 'enviado' : 'assinado'}.`;
  }
  return null;
}

/** D8: cancelar a assinatura nomeia o documento e diz o que acontece. */
export function confirmacaoDeCancelarAssinatura(doc: any): string {
  return [
    'Cancelar a assinatura?',
    `${doc?.code} - ${doc?.title}. O envelope no ClickSign é cancelado (quem não assinou não consegue mais), e o documento volta para Aprovado.`,
  ].join('\n\n');
}
